/* tslint:disable */
/* eslint-disable */
import { HttpClient, HttpContext } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { BaseService } from '../base-service';
import { ApiConfiguration } from '../api-configuration';
import { StrictHttpResponse } from '../strict-http-response';

import { ApiResponseContact } from '../models/api-response-contact';
import { ApiResponseContactSoftDeleteByPhoneResult } from '../models/api-response-contact-soft-delete-by-phone-result';
import { ApiResponseContactSyncUpdateResult } from '../models/api-response-contact-sync-update-result';
import { ApiResponseContactUploadBatchResponse } from '../models/api-response-contact-upload-batch-response';
import { ApiResponseListContact } from '../models/api-response-list-contact';
import { ApiResponsePagedResultContact } from '../models/api-response-paged-result-contact';
import { createContact } from '../fn/contact-api/create-contact';
import { CreateContact$Params } from '../fn/contact-api/create-contact';
import { deleteContact } from '../fn/contact-api/delete-contact';
import { DeleteContact$Params } from '../fn/contact-api/delete-contact';
import { exportContacts } from '../fn/contact-api/export-contacts';
import { ExportContacts$Params } from '../fn/contact-api/export-contacts';
import { exportFailedContacts } from '../fn/contact-api/export-failed-contacts';
import { ExportFailedContacts$Params } from '../fn/contact-api/export-failed-contacts';
import { getContactByReference } from '../fn/contact-api/get-contact-by-reference';
import { GetContactByReference$Params } from '../fn/contact-api/get-contact-by-reference';
import { getContactOfConversation } from '../fn/contact-api/get-contact-of-conversation';
import { GetContactOfConversation$Params } from '../fn/contact-api/get-contact-of-conversation';
import { getUploadBatchStatus } from '../fn/contact-api/get-upload-batch-status';
import { GetUploadBatchStatus$Params } from '../fn/contact-api/get-upload-batch-status';
import { listContacts } from '../fn/contact-api/list-contacts';
import { ListContacts$Params } from '../fn/contact-api/list-contacts';
import { searchContacts } from '../fn/contact-api/search-contacts';
import { SearchContacts$Params } from '../fn/contact-api/search-contacts';
import { softDeleteContactsByPhoneNumber } from '../fn/contact-api/soft-delete-contacts-by-phone-number';
import { SoftDeleteContactsByPhoneNumber$Params } from '../fn/contact-api/soft-delete-contacts-by-phone-number';
import { syncUpdateContacts } from '../fn/contact-api/sync-update-contacts';
import { SyncUpdateContacts$Params } from '../fn/contact-api/sync-update-contacts';
import { updateContact } from '../fn/contact-api/update-contact';
import { UpdateContact$Params } from '../fn/contact-api/update-contact';
import { uploadContacts } from '../fn/contact-api/upload-contacts';
import { UploadContacts$Params } from '../fn/contact-api/upload-contacts';

@Injectable({ providedIn: 'root' })
export class ContactApiService extends BaseService {
  constructor(config: ApiConfiguration, http: HttpClient) {
    super(config, http);
  }

  /** Path part for operation `getContactByReference()` */
  static readonly GetContactByReferencePath = '/v1/api/ui/contacts/{reference}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContactByReference()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactByReference$Response(params: GetContactByReference$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseContact>> {
    return getContactByReference(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContactByReference$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactByReference(params: GetContactByReference$Params, context?: HttpContext): Observable<ApiResponseContact> {
    return this.getContactByReference$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseContact>): ApiResponseContact => r.body)
    );
  }

  /** Path part for operation `updateContact()` */
  static readonly UpdateContactPath = '/v1/api/ui/contacts/{reference}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `updateContact()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContact$Response(params: UpdateContact$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseContact>> {
    return updateContact(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `updateContact$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  updateContact(params: UpdateContact$Params, context?: HttpContext): Observable<ApiResponseContact> {
    return this.updateContact$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseContact>): ApiResponseContact => r.body)
    );
  }

  /** Path part for operation `deleteContact()` */
  static readonly DeleteContactPath = '/v1/api/ui/contacts/{reference}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `deleteContact()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteContact$Response(params: DeleteContact$Params, context?: HttpContext): Observable<StrictHttpResponse<void>> {
    return deleteContact(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `deleteContact$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  deleteContact(params: DeleteContact$Params, context?: HttpContext): Observable<void> {
    return this.deleteContact$Response(params, context).pipe(
      map((r: StrictHttpResponse<void>): void => r.body)
    );
  }

  /** Path part for operation `createContact()` */
  static readonly CreateContactPath = '/v1/api/ui/contacts';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `createContact()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createContact$Response(params: CreateContact$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseContact>> {
    return createContact(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `createContact$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  createContact(params: CreateContact$Params, context?: HttpContext): Observable<ApiResponseContact> {
    return this.createContact$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseContact>): ApiResponseContact => r.body)
    );
  }

  /** Path part for operation `uploadContacts()` */
  static readonly UploadContactsPath = '/v1/api/ui/contacts/upload';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `uploadContacts()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  uploadContacts$Response(params: UploadContacts$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseContactUploadBatchResponse>> {
    return uploadContacts(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `uploadContacts$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  uploadContacts(params: UploadContacts$Params, context?: HttpContext): Observable<ApiResponseContactUploadBatchResponse> {
    return this.uploadContacts$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseContactUploadBatchResponse>): ApiResponseContactUploadBatchResponse => r.body)
    );
  }

  /** Path part for operation `syncUpdateContacts()` */
  static readonly SyncUpdateContactsPath = '/v1/api/ui/contacts/sync-update';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `syncUpdateContacts()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  syncUpdateContacts$Response(params: SyncUpdateContacts$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseContactSyncUpdateResult>> {
    return syncUpdateContacts(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `syncUpdateContacts$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  syncUpdateContacts(params: SyncUpdateContacts$Params, context?: HttpContext): Observable<ApiResponseContactSyncUpdateResult> {
    return this.syncUpdateContacts$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseContactSyncUpdateResult>): ApiResponseContactSyncUpdateResult => r.body)
    );
  }

  /** Path part for operation `searchContacts()` */
  static readonly SearchContactsPath = '/v1/api/ui/contacts/search';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `searchContacts()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchContacts$Response(params: SearchContacts$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponsePagedResultContact>> {
    return searchContacts(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `searchContacts$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  searchContacts(params: SearchContacts$Params, context?: HttpContext): Observable<ApiResponsePagedResultContact> {
    return this.searchContacts$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponsePagedResultContact>): ApiResponsePagedResultContact => r.body)
    );
  }

  /** Path part for operation `listContacts()` */
  static readonly ListContactsPath = '/v1/api/ui/contacts/list';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `listContacts()` instead.
   *
   * This method doesn't expect any request body.
   */
  listContacts$Response(params: ListContacts$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseListContact>> {
    return listContacts(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `listContacts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  listContacts(params: ListContacts$Params, context?: HttpContext): Observable<ApiResponseListContact> {
    return this.listContacts$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseListContact>): ApiResponseListContact => r.body)
    );
  }

  /** Path part for operation `exportContacts()` */
  static readonly ExportContactsPath = '/v1/api/ui/contacts/export';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportContacts()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  exportContacts$Response(params: ExportContacts$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return exportContacts(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportContacts$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  exportContacts(params: ExportContacts$Params, context?: HttpContext): Observable<Blob> {
    return this.exportContacts$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `softDeleteContactsByPhoneNumber()` */
  static readonly SoftDeleteContactsByPhoneNumberPath = '/v1/api/ui/contacts/delete-by-phone';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `softDeleteContactsByPhoneNumber()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  softDeleteContactsByPhoneNumber$Response(params: SoftDeleteContactsByPhoneNumber$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseContactSoftDeleteByPhoneResult>> {
    return softDeleteContactsByPhoneNumber(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `softDeleteContactsByPhoneNumber$Response()` instead.
   *
   * This method sends `application/json` and handles request body of type `application/json`.
   */
  softDeleteContactsByPhoneNumber(params: SoftDeleteContactsByPhoneNumber$Params, context?: HttpContext): Observable<ApiResponseContactSoftDeleteByPhoneResult> {
    return this.softDeleteContactsByPhoneNumber$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseContactSoftDeleteByPhoneResult>): ApiResponseContactSoftDeleteByPhoneResult => r.body)
    );
  }

  /** Path part for operation `exportFailedContacts()` */
  static readonly ExportFailedContactsPath = '/v1/api/ui/contacts/upload/{batchReference}/failed-export';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `exportFailedContacts()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportFailedContacts$Response(params: ExportFailedContacts$Params, context?: HttpContext): Observable<StrictHttpResponse<Blob>> {
    return exportFailedContacts(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `exportFailedContacts$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  exportFailedContacts(params: ExportFailedContacts$Params, context?: HttpContext): Observable<Blob> {
    return this.exportFailedContacts$Response(params, context).pipe(
      map((r: StrictHttpResponse<Blob>): Blob => r.body)
    );
  }

  /** Path part for operation `getUploadBatchStatus()` */
  static readonly GetUploadBatchStatusPath = '/v1/api/ui/contacts/upload/status/{batchReference}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getUploadBatchStatus()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUploadBatchStatus$Response(params: GetUploadBatchStatus$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseContactUploadBatchResponse>> {
    return getUploadBatchStatus(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getUploadBatchStatus$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getUploadBatchStatus(params: GetUploadBatchStatus$Params, context?: HttpContext): Observable<ApiResponseContactUploadBatchResponse> {
    return this.getUploadBatchStatus$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseContactUploadBatchResponse>): ApiResponseContactUploadBatchResponse => r.body)
    );
  }

  /** Path part for operation `getContactOfConversation()` */
  static readonly GetContactOfConversationPath = '/v1/api/ui/contacts/of-conversation/{reference}';

  /**
   * This method provides access to the full `HttpResponse`, allowing access to response headers.
   * To access only the response body, use `getContactOfConversation()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactOfConversation$Response(params: GetContactOfConversation$Params, context?: HttpContext): Observable<StrictHttpResponse<ApiResponseContact>> {
    return getContactOfConversation(this.http, this.rootUrl, params, context);
  }

  /**
   * This method provides access only to the response body.
   * To access the full response (for headers, for example), `getContactOfConversation$Response()` instead.
   *
   * This method doesn't expect any request body.
   */
  getContactOfConversation(params: GetContactOfConversation$Params, context?: HttpContext): Observable<ApiResponseContact> {
    return this.getContactOfConversation$Response(params, context).pipe(
      map((r: StrictHttpResponse<ApiResponseContact>): ApiResponseContact => r.body)
    );
  }

}
